/**
 * client/src/pages/SignIn.tsx — the door.
 *
 * Sign in and sign up on one screen: a private site for a dozen people does not need two routes and
 * a "don't have an account?" round trip.
 *
 * Two facts in the copy are load-bearing, and people get both wrong otherwise:
 *
 *  1. **One karti.ai account.** Supabase is the credential store for six sibling sites and the email
 *     and password are shared with all of them (§6.2). Someone arriving from books or movies must use
 *     their existing password — signup refuses a conflicting address rather than adopting it, because
 *     "adopt the conflicting auth user" is an account-takeover primitive for anyone holding the shared
 *     invite code, and og.karti.ai shipped exactly that. Saying so beforehand is the difference
 *     between "it linked" and "it's broken".
 *  2. **It is invite-only, and the invite is checked first.** §6.2 makes the order of operations the
 *     design: invite check before anything touches the network, because an ungated form lets a bot
 *     burn the auth budget *for the whole karti.ai family* and lock Karti out of movies.
 *
 * ⚠️ **"Wrong password" and "the auth provider is down" are never the same sentence.** LAW 16: only
 * `ok` and `wrong` advance the state machine; `throttled` and `unavailable` come back as 503. Telling
 * someone their password is wrong when Supabase is throttled sends them off to reset a password that
 * was fine, and the reset goes through the same throttled bucket.
 */

import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SiteMark } from "../App.tsx";
import { ApiError, abApi, refusalCopy } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";

type Mode = "signin" | "signup";

interface Trouble {
  title: string;
  fix?: string;
}

export function SignIn() {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [mode, setMode] = useState<Mode>("signin");
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState<Trouble | null>(null);

  const [who, setWho] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [handle, setHandle] = useState("");
  const [invite, setInvite] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setTrouble(null);
    try {
      const res =
        mode === "signin"
          ? await abApi.signIn(who.trim(), password)
          : await abApi.signUp({
              email: email.trim(),
              handle: handle.trim().toLowerCase(),
              password,
              invite: invite.trim(),
            });
      adopt(res);
      navigate(from, { replace: true });
    } catch (err) {
      setTrouble(troubleFor(err, mode));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ab-door">
      <div className="ab-door-inner">
        <div className="ab-door-mark">
          <SiteMark />
          <p>
            A private library. Upload a book, read it on the phone in your pocket, and hear it in
            Karti&rsquo;s own voice.
          </p>
        </div>

        <form className="ab-door-form" onSubmit={(e) => void submit(e)}>
          <div className="ab-segment ab-segment-full" role="tablist" aria-label="Sign in or make an account">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signin"}
              className={mode === "signin" ? "ab-segment-item is-on" : "ab-segment-item"}
              onClick={() => {
                setMode("signin");
                setTrouble(null);
              }}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signup"}
              className={mode === "signup" ? "ab-segment-item is-on" : "ab-segment-item"}
              onClick={() => {
                setMode("signup");
                setTrouble(null);
              }}
            >
              New account
            </button>
          </div>

          {mode === "signin" ? (
            <div className="ab-field">
              <label htmlFor="ab-who">Username or email</label>
              <input
                id="ab-who"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                autoFocus
                required
                value={who}
                onChange={(e) => setWho(e.target.value)}
              />
            </div>
          ) : (
            <>
              <div className="ab-field">
                <label htmlFor="ab-invite">Invite code</label>
                <input
                  id="ab-invite"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoFocus
                  required
                  value={invite}
                  onChange={(e) => setInvite(e.target.value)}
                />
                <p className="ab-hint">Checked before anything else happens. Ask Karti for it.</p>
              </div>
              <div className="ab-field">
                <label htmlFor="ab-email">Email</label>
                <input
                  id="ab-email"
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="ab-field">
                <label htmlFor="ab-handle">Username</label>
                <input
                  id="ab-handle"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                  placeholder="2–24 characters"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="ab-field">
            <label htmlFor="ab-password">Password</label>
            <input
              id="ab-password"
              type="password"
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {mode === "signup" && (
              <p className="ab-hint">
                One account across every karti.ai site. If you already have one, sign in with that
                password instead — a new one here will be refused rather than quietly overwrite it.
              </p>
            )}
          </div>

          {trouble && (
            <div className="ab-refusal" role="alert">
              <div>
                <strong>{trouble.title}</strong>
                {trouble.fix && <p>{trouble.fix}</p>}
              </div>
            </div>
          )}

          <button type="submit" className="ab-btn ab-btn-primary ab-btn-block" disabled={busy}>
            {busy ? "One moment…" : mode === "signin" ? "Sign in" : "Make an account"}
          </button>
        </form>

        <p className="ab-door-foot">
          <Link to="/" className="ab-link">
            Back to the front page
          </Link>
        </p>
      </div>
    </div>
  );
}

function troubleFor(err: unknown, mode: Mode): Trouble {
  if (!(err instanceof ApiError)) return { title: "Something went wrong. Try again." };

  if (err.offline) {
    return {
      title: "That never reached the server.",
      fix: "Check your connection and try again — nothing was changed.",
    };
  }

  // LAW 16, at the surface. 503 is "the credential store did not answer or is throttling us", and it
  // must never be spelled as a wrong password. The shared bucket is ~30 auth requests per 5 minutes
  // across six sites, so this is a real state, not a theoretical one.
  if (err.upstreamDown) {
    return {
      title: "Sign-in is temporarily unavailable.",
      fix: "The account service isn't answering right now. Your password is fine — wait a minute and try again.",
    };
  }

  if (err.status === 429) {
    return {
      title: "Too many attempts.",
      fix: err.message.includes("try") ? err.message : "Wait a few minutes before trying again.",
    };
  }

  const known = refusalCopy(err.reason);
  if (known) return known;

  if (err.status === 401 || err.status === 403) {
    return mode === "signin"
      ? {
          title: "That username and password don't match.",
          fix: "It is the same password as the other karti.ai sites — try the one you use there.",
        }
      : { title: err.message };
  }
  return { title: err.message };
}
